<template>
  <section class="bar-rank-options" :aria-label="axisStyle ? '横向统计条设置' : '横向比例条设置'">
    <p v-if="axisStyle">左侧保留轴线，右侧显示数值和单位。最大值填满底槽，其余数值按相同比例展示。</p>
    <p v-else>按所选数值系列展示，右侧占比按各分类的非负数值合计计算。横向布局由样式自动排版。</p>
    <el-form-item v-if="series.length > 1" label="显示系列" for="screen-bar-rank-series">
      <el-select id="screen-bar-rank-series" :value="settings.barRankSeriesIndex" :disabled="main.activeLayoutLocked" :aria-label="styleLabel + '显示系列'" @input="set('barRankSeriesIndex', $event)">
        <el-option v-for="(item, index) in series" :key="index" :label="item.name || ('系列' + (index + 1))" :value="index" />
      </el-select>
    </el-form-item>
    <el-form-item v-if="!axisStyle" label="条长基准" for="screen-bar-rank-scale">
      <el-select id="screen-bar-rank-scale" :value="settings.barRankScale" :disabled="main.activeLayoutLocked" aria-label="比例条长度基准" @input="set('barRankScale', $event)">
        <el-option label="按实际占比" value="total" />
        <el-option label="最大值填满" value="max" />
      </el-select>
    </el-form-item>
    <el-form-item label="数量单位">
      <el-input :value="settings.barRankUnit" :disabled="main.activeLayoutLocked" :label="styleLabel + '数量单位'" placeholder="如：台、次、kWh；可留空" @input="set('barRankUnit', $event)" />
    </el-form-item>
    <el-form-item label="左侧文字宽度">
      <el-input-number :value="settings.barRankLabelWidth" :min="0" :max="1000" :precision="0" controls-position="right" :disabled="main.activeLayoutLocked" :label="styleLabel + '左侧文字宽度'" @input="set('barRankLabelWidth', $event)" />
    </el-form-item>
    <p>单位为 px，0 表示自动匹配最长文字。优先保留左侧文字宽度，空间不足时先压缩比例条；超出可用宽度的文字以省略号显示。</p>
    <el-form-item v-if="!axisStyle" label="显示占比">
      <el-switch :value="settings.barRankShowPercent" :disabled="main.activeLayoutLocked" aria-label="比例条显示占比" @change="set('barRankShowPercent', $event)" />
    </el-form-item>
    <el-form-item v-for="field in visibleNumbers" :key="field.key" :label="field.label">
      <el-input-number :value="settings[field.key]" :min="field.min" :max="field.max" :precision="0" controls-position="right" :disabled="main.activeLayoutLocked" :label="styleLabel + field.label" @input="set(field.key, $event)" />
    </el-form-item>
    <el-form-item v-for="field in visibleColors" :key="field.key" :label="field.label">
      <div class="bar-rank-options__color">
        <el-input :value="colorInput(field.key)" :disabled="main.activeLayoutLocked" :label="styleLabel + field.label" @input="set(field.key, $event)" />
        <el-color-picker :value="validColor(colorInput(field.key)) || null" :disabled="main.activeLayoutLocked" show-alpha :aria-label="'选择' + styleLabel + field.label" @change="set(field.key, $event || defaults[field.key])" />
      </div>
    </el-form-item>
    <p>圆角为 0 时显示直角，最大不超过实际条高的一半。彩色条沿用下方配色；开启数据流动后，流光仅在条内播放。</p>
  </section>
</template>
<script>
import { BAR_RANK_DEFAULTS, BAR_AXIS_DEFAULTS, barRankSettings } from '@/runtime/bar-rank'
import { barCategoryColor } from '@/runtime/bar-presentation'
export default {
  inject: ['main'],
  props: { series: { type: Array, default: () => [] } },
  data: () => ({
    defaults: { ...BAR_RANK_DEFAULTS, ...BAR_AXIS_DEFAULTS },
    numbers: [
      { key: 'barRankHeight', label: '条高', min: 2, max: 80 },
      { key: 'barRankRadius', label: '圆角', min: 0, max: 40 },
      { key: 'barRankFontSize', label: '文字大小', min: 8, max: 60 },
      { key: 'barRankGap', label: '列间距', min: 2, max: 60 },
      { key: 'barRankPrecision', label: '占比小数位', min: 0, max: 4 },
      { key: 'barAxisWidth', label: '轴线宽度', min: 1, max: 4 }
    ],
    colors: [
      { key: 'barRankTrackColor', label: '底槽颜色' },
      { key: 'barRankNameColor', label: '分类颜色' },
      { key: 'barRankValueColor', label: '数量颜色' },
      { key: 'barRankPercentColor', label: '占比颜色' },
      { key: 'barAxisColor', label: '轴线颜色' }
    ]
  }),
  computed: {
    axisStyle () { return this.main.activeOption.barStyle === 'axis' },
    styleLabel () { return this.axisStyle ? '统计条' : '比例条' },
    settings () { return barRankSettings(this.main.activeOption) },
    visibleNumbers () { return this.numbers.filter(field => (field.key !== 'barRankPrecision' || this.settings.barRankShowPercent) && (field.key !== 'barAxisWidth' || this.axisStyle)) },
    visibleColors () { return this.colors.filter(field => (field.key !== 'barRankPercentColor' || this.settings.barRankShowPercent) && (field.key !== 'barAxisColor' || this.axisStyle)) }
  },
  methods: {
    validColor: barCategoryColor,
    colorInput (key) { return this.main.activeOption[key] == null ? this.defaults[key] : this.main.activeOption[key] },
    set (key, value) { if (!this.main.activeLayoutLocked) this.$set(this.main.activeOption, key, value) }
  }
}
</script>
<style scoped>
.bar-rank-options p { margin: 0 0 12px; color: var(--editor-muted, #a4b6c5); font-size: 11px; line-height: 1.65; overflow-wrap: anywhere; }
.bar-rank-options .el-select, .bar-rank-options .el-input-number { width: 100%; }
.bar-rank-options__color { display: flex; align-items: center; gap: 6px; min-width: 0; }
.bar-rank-options__color .el-input { flex: 1; min-width: 0; }
.bar-rank-options__color .el-color-picker { flex: none; }
</style>
